import { Component, type ErrorInfo, type ReactNode } from 'react';
import buttonStyles from '../styles/Button.module.css';
import styles from '../styles/ErrorBoundary.module.css';
import { reloadOnce, reportClientError } from '../utils/clientError';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  crashed: boolean;
  reloading: boolean;
  // Shown in small print, so a visitor can send a screenshot of it
  details?: string;
}

// Catches a crash anywhere on the page, instead of Next.js's black "Application
// error" screen: reports it and reloads once (see utils/clientError.ts)
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { crashed: false, reloading: false };

  static getDerivedStateFromError(error: unknown) {
    const err = error instanceof Error ? error : new Error(String(error));
    const where = err.stack?.split('\n').slice(0, 3).join('\n') ?? '';
    return { crashed: true, details: `${err.name}: ${err.message}\n${where}` };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportClientError(error, info.componentStack);
    if (reloadOnce()) this.setState({ reloading: true });
  }

  render() {
    if (!this.state.crashed) return this.props.children;
    if (this.state.reloading) return null;
    return (
      <div className={styles.crashed}>
        <p>Sorry, something went wrong loading this page.</p>
        <button type="button" className={buttonStyles.button} onClick={() => window.location.reload()}>
          Reload
        </button>
        {this.state.details && <pre className={styles.details}>{this.state.details}</pre>}
      </div>
    );
  }
}

export default ErrorBoundary;
